import { apiClient } from './api';
import type {
  CanteenCancelResult,
  CanteenChangeResult,
  CanteenOrder,
  CanteenPlanRequest,
  CanteenPlanResult,
  CanteenPlanner,
  MyWallets,
  PayAndTopUp,
  PayResult,
  PayStarted,
  TopUpStarted,
  Wallet,
  WalletCard,
  WalletStatement,
} from '../types/wallet';

// The family's wallets: every child's wallet the signed-in parent may see,
// scoped by the server (StudentRelatives). Online top-up and Pay & top up are
// offered only where the school has a payment gateway - the server says so
// through onlineTopUp / onlinePayAvailable; otherwise the family pays at the
// school office quoting the Payer ID.
export const walletAPI = {
  getMyWallets: async () => (await apiClient.get<MyWallets>('/wallet/my')).data,

  getStatement: async (walletId: string) =>
    (await apiClient.get<WalletStatement>(`/wallet/my/${walletId}`)).data,

  /** A lost card stops working at once; the school issues a new one. */
  blockCard: async (credentialId: string, reason?: string) =>
    (await apiClient.post<WalletCard>(`/wallet/my/cards/${credentialId}/block`, { reason })).data,

  startTopUp: async (walletId: string, amount: number) =>
    (await apiClient.post<TopUpStarted>('/wallet/my/topup/start', { walletId, amount })).data,

  confirmTopUp: async (intentId: string, gatewayCallback: Record<string, string>) =>
    (await apiClient.post<Wallet>('/wallet/my/topup/confirm', { intentId, gatewayCallback })).data,

  getPayAndTopUp: async () => (await apiClient.get<PayAndTopUp>('/wallet/my/pay')).data,

  startPay: async (body: { invoiceIds: string[]; topUps: { walletId: string; amount: number }[] }) =>
    (await apiClient.post<PayStarted>('/wallet/my/pay/start', body)).data,

  confirmPay: async (basketId: string, gatewayCallback: Record<string, string>) =>
    (await apiClient.post<PayResult>('/wallet/my/pay/confirm', { basketId, gatewayCallback })).data,

  getPayResult: async (basketId: string) =>
    (await apiClient.get<PayResult>(`/wallet/my/pay/${basketId}`)).data,
};

// The canteen for one child (api/canteen/my). The server checks the child is
// the caller's own (403 otherwise) and prices every choice itself; orders are
// paid from the child's wallet only, and a short balance is a 409 whose message
// tells the parent to top up first.
export const canteenAPI = {
  getOrders: async (studentId: string) =>
    (await apiClient.get<CanteenOrder[]>(`/canteen/my/${studentId}/orders`)).data,

  /** Every order for one month ("2026-10"), newest first - the Canteen screen's Order history. */
  getOrdersForMonth: async (studentId: string, month: string) =>
    (await apiClient.get<CanteenOrder[]>(`/canteen/my/${studentId}/orders`, { params: { month } })).data,

  /** The week planner from a Monday (YYYY-MM-DD), `days` days long. */
  getPlanner: async (studentId: string, from: string, days = 7) =>
    (await apiClient.get<CanteenPlanner>(`/canteen/my/${studentId}/planner`, { params: { from, days } })).data,

  /** Places every chosen (date, slot, option) at once - all or nothing. */
  placePlan: async (studentId: string, body: CanteenPlanRequest) =>
    (await apiClient.post<CanteenPlanResult>(`/canteen/my/${studentId}/planner`, body)).data,

  /** Swaps an ordered meal for another dish of the same meal (tomorrow onwards, before the cut-off). */
  changeOrder: async (studentId: string, canteenOrderId: string, optionNo: number) =>
    (await apiClient.post<CanteenChangeResult>(`/canteen/my/${studentId}/orders/${canteenOrderId}/change`, { optionNo })).data,

  cancelOrder: async (studentId: string, canteenOrderId: string) =>
    (await apiClient.post<CanteenCancelResult>(`/canteen/my/${studentId}/orders/${canteenOrderId}/cancel`)).data,
};

/** The server's own sentence when it sent one (400/403 { message }), else the fallback. */
export function serverMessage(error: unknown, fallback: string): string {
  const message = (error as any)?.response?.data?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
}

/**
 * What to tell the parent when a payment step fails: the server's sentence for
 * an HTTP failure (a bare "Network Error" never reaches them), the gateway's own
 * sentence for a checkout failure, else the fallback.
 */
export function paymentErrorText(error: unknown, fallback: string): string {
  const e = error as any;
  if (e?.isAxiosError || e?.response) { return serverMessage(error, fallback); }
  const message = typeof e?.message === 'string' ? e.message.trim() : '';
  return message || fallback;
}
